import { useState } from 'react';
import { Send, MapPin, Package, Calendar, Hash, ArrowRight } from 'lucide-react';
import Layout from '@/components/Layout';
import { Card } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Label } from '@/components/ui/label';
import { toast } from 'sonner';
import { Link } from 'react-router-dom';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function QuotePage() {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    company: '',
    pickup_location: '',
    delivery_location: '',
    pickup_date: '',
    quantity: 1,
    description: ''
  });
  
  // Array to hold weight and dimensions for each item
  const [items, setItems] = useState([
    { weight: '', dimensions: '' }
  ]);
  
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleChange = (e) => {
    const { name, value } = e.target;
    
    if (name === 'quantity') {
      const qty = parseInt(value) || 1;
      setFormData({ ...formData, [name]: value });
      
      // Adjust items array based on quantity
      if (qty > items.length) {
        // Add more items
        const newItems = [...items];
        for (let i = items.length; i < qty; i++) {
          newItems.push({ weight: '', dimensions: '' });
        }
        setItems(newItems);
      } else if (qty < items.length) {
        // Remove extra items
        setItems(items.slice(0, qty));
      }
    } else {
      setFormData({ ...formData, [name]: value });
    }
  };

  const handleItemChange = (index, field, value) => {
    const newItems = [...items];
    newItems[index][field] = value;
    setItems(newItems);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Basic validation
    if (!formData.name || !formData.email || !formData.phone || !formData.pickup_location || !formData.delivery_location) {
      toast.error('Please fill in all required fields');
      return;
    }

    setIsSubmitting(true);
    
    try {
      // Calculate total weight and create dimensions string
      const totalWeight = items.reduce((sum, item) => {
        const weight = parseFloat(item.weight) || 0;
        return sum + weight;
      }, 0);
      
      // Create a combined dimensions string
      const dimensionsString = items
        .map((item, idx) => item.dimensions ? `#${idx + 1}: ${item.dimensions}` : '')
        .filter(d => d)
        .join('; ');

      const response = await fetch(`${API}/quotes`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          name: formData.name,
          email: formData.email,
          phone: formData.phone,
          company: formData.company || null,
          pickup_location: formData.pickup_location,
          delivery_location: formData.delivery_location,
          pickup_date: formData.pickup_date || null,
          quantity: parseInt(formData.quantity) || 1,
          weight: totalWeight > 0 ? totalWeight : null,
          dimensions: dimensionsString || null,
          description: formData.description || null
        })
      });

      if (!response.ok) {
        throw new Error('Failed to submit quote request');
      }

      toast.success('Quote request submitted successfully! We\'ll contact you within 2 hours.');
      
      // Reset form
      setFormData({
        name: '',
        email: '',
        phone: '',
        company: '',
        pickup_location: '',
        delivery_location: '',
        pickup_date: '',
        quantity: 1,
        description: ''
      });
      setItems([{ weight: '', dimensions: '' }]);
    } catch (error) {
      console.error('Error submitting quote:', error);
      toast.error('Failed to submit quote request. Please try again or call us directly.');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Layout>
      {/* Hero Section */}
      <section className="py-20 relative overflow-hidden">
        <div className="absolute top-0 left-1/2 -translate-x-1/2 w-[800px] h-[600px] bg-primary/10 blur-[120px] rounded-full" />
        
        <div className="container mx-auto px-4 relative z-10">
          <div className="text-center mb-16 animate-slide-up">
            <Badge variant="outline" className="mb-4 border-primary/50 text-primary bg-primary/10">
              Get Started
            </Badge>
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold mb-6">
              Request a <span className="text-gradient">Free Quote</span>
            </h1>
            <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
              Tell us about your shipping needs, and we'll provide a competitive quote within 2 hours during business hours.
            </p>
          </div>
        </div>
      </section>

      {/* Quote Form */}
      <section className="pb-24 relative overflow-hidden">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <Card className="scroll-animate p-8 md:p-12 bg-card/80 backdrop-blur-sm border-border/50">
              <form onSubmit={handleSubmit} className="space-y-6">
                {/* Contact Information */}
                <div>
                  <h3 className="text-xl font-semibold mb-4">Contact Information</h3>
                  <div className="grid md:grid-cols-2 gap-6">
                    <div className="space-y-2">
                      <Label htmlFor="name" className="text-sm font-medium">
                        Full Name <span className="text-destructive">*</span>
                      </Label>
                      <Input
                        id="name"
                        name="name"
                        value={formData.name}
                        onChange={handleChange}
                        placeholder="John Doe"
                        className="bg-background border-border/50 focus:border-primary transition-colors"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="company" className="text-sm font-medium">
                        Company Name
                      </Label>
                      <Input
                        id="company"
                        name="company"
                        value={formData.company}
                        onChange={handleChange}
                        placeholder="Your Company Inc."
                        className="bg-background border-border/50 focus:border-primary transition-colors"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="email" className="text-sm font-medium">
                        Email Address <span className="text-destructive">*</span>
                      </Label>
                      <Input
                        id="email"
                        name="email"
                        type="email"
                        value={formData.email}
                        onChange={handleChange}
                        placeholder="john@company.com"
                        className="bg-background border-border/50 focus:border-primary transition-colors"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="phone" className="text-sm font-medium">
                        Phone Number <span className="text-destructive">*</span>
                      </Label>
                      <Input
                        id="phone"
                        name="phone"
                        type="tel"
                        value={formData.phone}
                        onChange={handleChange}
                        placeholder="(416) 555-0123"
                        className="bg-background border-border/50 focus:border-primary transition-colors"
                      />
                    </div>
                  </div>
                </div>

                {/* Shipment Details */}
                <div>
                  <h3 className="text-xl font-semibold mb-4">Shipment Details</h3>
                  <div className="space-y-6">
                    <div className="grid md:grid-cols-2 gap-6">
                      <div className="space-y-2">
                        <Label htmlFor="pickup_location" className="text-sm font-medium flex items-center gap-2">
                          <MapPin className="h-4 w-4 text-primary" />
                          Pickup Location <span className="text-destructive">*</span>
                        </Label>
                        <Input
                          id="pickup_location"
                          name="pickup_location"
                          value={formData.pickup_location}
                          onChange={handleChange}
                          placeholder="123 Main St, Toronto, ON M5V 1A1"
                          className="bg-background border-border/50 focus:border-primary transition-colors"
                        />
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="delivery_location" className="text-sm font-medium flex items-center gap-2">
                          <MapPin className="h-4 w-4 text-secondary" />
                          Delivery Location <span className="text-destructive">*</span>
                        </Label>
                        <Input
                          id="delivery_location"
                          name="delivery_location"
                          value={formData.delivery_location}
                          onChange={handleChange}
                          placeholder="456 King St, Mississauga, ON L5A 2B3"
                          className="bg-background border-border/50 focus:border-primary transition-colors"
                        />
                      </div>
                    </div>

                    <div className="grid md:grid-cols-2 gap-6">
                      <div className="space-y-2">
                        <Label htmlFor="quantity" className="text-sm font-medium flex items-center gap-2">
                          <Hash className="h-4 w-4 text-primary" />
                          Quantity <span className="text-destructive">*</span>
                        </Label>
                        <Input
                          id="quantity"
                          name="quantity"
                          type="number"
                          min="1"
                          max="50"
                          value={formData.quantity}
                          onChange={handleChange}
                          placeholder="1"
                          className="bg-background border-border/50 focus:border-primary transition-colors"
                        />
                        <p className="text-xs text-muted-foreground">Number of items to ship</p>
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="pickup_date" className="text-sm font-medium flex items-center gap-2">
                          <Calendar className="h-4 w-4 text-primary" />
                          Preferred Pickup Date
                        </Label>
                        <Input
                          id="pickup_date"
                          name="pickup_date"
                          type="date"
                          value={formData.pickup_date}
                          onChange={handleChange}
                          className="bg-background border-border/50 focus:border-primary transition-colors"
                        />
                      </div>
                    </div>
                  </div>
                </div>

                {/* Dynamic Item Fields */}
                {items.length > 0 && (
                  <div>
                    <h3 className="text-xl font-semibold mb-4">Item Details</h3>
                    <div className="space-y-4">
                      {items.map((item, index) => (
                        <Card key={index} className="p-4 bg-muted/30 border-border/50">
                          <div className="flex items-center gap-2 mb-3">
                            <div className="w-8 h-8 rounded-lg bg-primary/20 flex items-center justify-center">
                              <span className="text-sm font-semibold text-primary">#{index + 1}</span>
                            </div>
                            <h4 className="font-medium">Item {index + 1}</h4>
                          </div>
                          <div className="grid md:grid-cols-2 gap-4">
                            <div className="space-y-2">
                              <Label htmlFor={`weight-${index}`} className="text-sm font-medium">
                                Weight (lbs)
                              </Label>
                              <Input
                                id={`weight-${index}`}
                                type="number"
                                step="0.01"
                                value={item.weight}
                                onChange={(e) => handleItemChange(index, 'weight', e.target.value)}
                                placeholder="e.g., 50"
                                className="bg-background border-border/50 focus:border-primary transition-colors"
                              />
                            </div>
                            <div className="space-y-2">
                              <Label htmlFor={`dimensions-${index}`} className="text-sm font-medium">
                                Dimensions (L×W×H)
                              </Label>
                              <Input
                                id={`dimensions-${index}`}
                                value={item.dimensions}
                                onChange={(e) => handleItemChange(index, 'dimensions', e.target.value)}
                                placeholder="e.g., 24x24x24 inches"
                                className="bg-background border-border/50 focus:border-primary transition-colors"
                              />
                            </div>
                          </div>
                        </Card>
                      ))}
                    </div>
                    
                    <div className="mt-4 p-4 rounded-lg bg-primary/10 border border-primary/20">
                      <p className="text-sm">
                        <strong className="text-primary">Total Items:</strong> {items.length} | 
                        <strong className="text-primary ml-2">Total Weight:</strong> {items.reduce((sum, item) => sum + (parseFloat(item.weight) || 0), 0).toFixed(2)} lbs
                      </p>
                    </div>
                  </div>
                )}

                {/* Description */}
                <div>
                  <h3 className="text-xl font-semibold mb-4">Additional Information</h3>
                  <div className="space-y-2">
                    <Label htmlFor="description" className="text-sm font-medium flex items-center gap-2">
                      <Package className="h-4 w-4 text-primary" />
                      Shipment Description
                    </Label>
                    <Textarea
                      id="description"
                      name="description"
                      value={formData.description}
                      onChange={handleChange}
                      placeholder="Please provide details about your shipment (e.g., type of goods, special handling requirements, fragile items, etc.)"
                      rows={4}
                      className="bg-background border-border/50 focus:border-primary transition-colors resize-none"
                    />
                  </div>
                </div>

                {/* Submit Button */}
                <div className="pt-4">
                  <Button 
                    type="submit"
                    disabled={isSubmitting}
                    size="lg"
                    className="w-full bg-gradient-primary hover:shadow-glow-lg transition-all duration-300 text-lg group"
                  >
                    {isSubmitting ? 'Submitting...' : 'Request Quote'}
                    <Send className="ml-2 h-5 w-5 group-hover:translate-x-1 transition-transform" />
                  </Button>
                </div>

                {/* Info text */}
                <p className="text-sm text-muted-foreground text-center">
                  We'll review your request and get back to you within <span className="font-semibold text-foreground">2 hours</span> during business hours (Mon-Fri, 8AM-6PM EST).
                </p>
              </form>
            </Card>

            {/* Contact alternatives */}
            <div className="grid md:grid-cols-2 gap-6 mt-8">
              <Card className="scroll-animate p-6 bg-card/50 backdrop-blur-sm border-border/50 hover:border-primary/30 transition-all duration-300" style={{ animationDelay: '0.1s' }}>
                <div className="flex items-start gap-4">
                  <div className="p-3 rounded-xl bg-primary/10">
                    <svg className="h-6 w-6 text-primary" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z" />
                    </svg>
                  </div>
                  <div>
                    <h3 className="font-semibold mb-1">Call Us Directly</h3>
                    <p className="text-sm text-muted-foreground mb-2">Speak with our team for immediate assistance</p>
                    <a href="tel:(647)-642-0062" className="text-primary hover:text-primary/80 font-medium">
                      (647)-642-0062
                    </a>
                    <p className="text-xs text-muted-foreground mt-1">Available 24/7</p>
                  </div>
                </div>
              </Card>

              <Card className="scroll-animate p-6 bg-card/50 backdrop-blur-sm border-border/50 hover:border-primary/30 transition-all duration-300" style={{ animationDelay: '0.2s' }}>
                <div className="flex items-start gap-4">
                  <div className="p-3 rounded-xl bg-primary/10">
                    <svg className="h-6 w-6 text-primary" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                    </svg>
                  </div>
                  <div>
                    <h3 className="font-semibold mb-1">Email Us</h3>
                    <p className="text-sm text-muted-foreground mb-2">For detailed inquiries and documentation</p>
                    <a href="mailto:info@synclinelogistics.com" className="text-primary hover:text-primary/80 font-medium">
                      info@synclinelogistics.com
                    </a>
                    <p className="text-xs text-muted-foreground mt-1">Response within 2 hours</p>
                  </div>
                </div>
              </Card>
            </div>
          </div>
        </div>
      </section>

      {/* Why Choose Section */}
      <section className="pb-24 bg-muted/30 relative overflow-hidden">
        <div className="container mx-auto px-4">
          <div className="text-center mb-12 scroll-animate">
            <h2 className="text-3xl sm:text-4xl font-bold mb-4">
              Why Choose <span className="text-gradient">Syncline?</span>
            </h2>
          </div>

          <div className="grid md:grid-cols-3 gap-6 max-w-4xl mx-auto">
            {[
              { title: 'Competitive Pricing', desc: 'Transparent rates with no hidden fees' },
              { title: 'Fast Response', desc: 'Quote provided within 2 hours' },
              { title: 'Flexible Service', desc: 'Customized solutions for your needs' }
            ].map((item, index) => (
              <Card 
                key={index}
                className="scroll-animate p-6 bg-card/50 backdrop-blur-sm border-border/50 text-center"
                style={{ animationDelay: `${index * 0.1}s` }}
              >
                <h3 className="font-semibold text-lg mb-2">{item.title}</h3>
                <p className="text-sm text-muted-foreground">{item.desc}</p>
              </Card>
            ))}
          </div>

          <div className="text-center mt-12 scroll-animate">
            <p className="text-muted-foreground mb-4">Want to learn more about our services?</p>
            <Link to="/services">
              <Button variant="outline" className="border-primary/50 text-primary hover:bg-primary/10">
                View All Services
                <ArrowRight className="ml-2 h-4 w-4" />
              </Button>
            </Link>
          </div>
        </div>
      </section>
    </Layout>
  );
}
